import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import TakeLabel from "../../../../components/label/TakeLabel";
import MarkdownContent from "../../../../components/markdown/MarkdownContent";
import MarkdownMediaThumbnail from "../../../../components/markdown/MarkdownMediaThumbnail";
import { extractLeadMedia } from "../../../../components/markdown/markdownMedia";
import { type TodoNoteCard } from "../../../../app/services/notes";

/**
 * HOME 進行中ホイールに並べる共有 TIPS カード（ノート由来）の縦型大判カード。
 *
 * 旧 TodoTipCard の内容を縦型カードに再構成:
 *  - 右上: カテゴリの垂れ帯（必須 = blue / 通常 = gray）
 *  - 本文の先頭メディア（画像 / 動画 / 音声）を自動でサムネイルとして
 *    上部に表示（本文からは採用済みメディアを除去して二重表示を防ぐ）
 *  - タイトル + 本文（あふれた分は下端フェードで示し、選択中はカード内
 *    スクロールで最後まで読み切れる）
 * OK / 役に立った の操作は下部のアクションパネル（InProgressWheel 側）が担う。
 *
 * 選択（ホイール中央）中は、本文を読みやすくするためカードを拡大する。
 * 拡大率は呼び出し側（InProgressWheel）がビューポートから算出して渡す。
 * 上端は固定して下方向へ広がる。拡大タイミングは自前のスクロール監視ではなく
 * 親から渡される `settled`（RadioCardGroup の中央ナッジ完了通知）で駆動し、
 * スクロール中の拡大縮小のガタつきを防ぐ。
 */

const Frame = styled.div<{ $selected: boolean; $scale: number }>`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 12px;
  border: 1px solid
    ${({ $selected }) =>
      $selected ? "var(--soremo-blue)" : "var(--soremo-border)"};
  outline: 1px solid
    ${({ $selected }) =>
      $selected ? "var(--soremo-blue)" : "var(--soremo-border)"};
  background: #fff;
  overflow: hidden;
  transform: scale(${({ $scale }) => $scale});
  transform-origin: top center;
  transition: border-color 0.2s, outline-color 0.2s,
    transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
  display: flex;
  flex-direction: column;
`;

const Thumbnail = styled(MarkdownMediaThumbnail)`
  width: 100%;
  aspect-ratio: 16 / 9;
  flex-shrink: 0;
`;

/**
 * 本文エリア。選択中はカード内スクロールで読み切れるようにする
 * （`data-card-scroll` を付けると RadioCardGroup がホイール/タッチを
 * 乗っ取らずネイティブスクロールへパスする）。非選択カードは従来どおり
 * クリップのみ。フェードは「まだ下に続きがあるとき」だけ出す。
 */
const Body = styled.div<{ $scrollable: boolean }>`
  position: relative;
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: ${({ $scrollable }) => ($scrollable ? "auto" : "hidden")};
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

/**
 * 本文あふれ表示のフェード。スクロール追従のため Body の擬似要素ではなく
 * カード（Frame）側に固定で重ねる。
 */
const BottomFade = styled.div<{ $visible: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 40px;
  background: linear-gradient(transparent, #fff);
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.2s;
`;

const Title = styled.p.attrs({
  className: "heading-spacing u-line-height-150",
})`
  /* 垂れ帯（右上）と重ならないよう右に余白を取る */
  padding-right: 48px;
`;

/**
 * カード本文の Markdown。読み物カードなのでリンクも通常の文字と同じ
 * 見た目にし、ホバー時だけ soremo-blue でリンクであることを示す。
 * （MarkdownContainer 既定の soremo-blue 文字を上書きする）
 */
const CardMarkdown = styled(MarkdownContent)`
  && a {
    color: inherit;
  }

  @media (hover: hover) {
    && a:hover {
      color: var(--soremo-blue);
    }
  }
`;

export interface TipPortraitCardProps {
  card: TodoNoteCard;
  selected: boolean;
  /** 選択中の拡大率（ビューポートから算出。1 なら拡大しない）。 */
  enlargeScale?: number;
  /**
   * ホイールの中央ナッジが完了しているか（RadioCardGroup の onSettleChange 由来）。
   * スクロール中は false になり、カードは等倍へ戻る。未指定なら選択中は常に拡大。
   */
  settled?: boolean;
}

const TipPortraitCard: React.FC<TipPortraitCardProps> = ({
  card,
  selected,
  enlargeScale = 1,
  settled = true,
}) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);

  // 選択中かつナッジ完了時のみ拡大する（スクロール再開で即座に等倍へ戻る）。
  const enlarged = selected && settled;

  // サムネイル = 本文の先頭メディア（自動採用）。本文からは取り除く。
  const { media: leadMedia, content: bodyContent } = useMemo(
    () => extractLeadMedia(card.content),
    [card.content],
  );

  // 下端フェードの表示判定。あふれが無い / 末端まで読んだら消す。
  const updateOverflowState = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    setHasMoreBelow(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  // 本文量・カードサイズの変化（画像ロードや折返し変化を含む）に追従する。
  useEffect(() => {
    updateOverflowState();
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateOverflowState);
    observer.observe(el);
    Array.from(el.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [updateOverflowState, bodyContent]);

  // 選択が外れたら読みかけ位置を先頭に戻す（縮小カードが途中で切れないように）。
  useEffect(() => {
    if (!selected && bodyRef.current) {
      bodyRef.current.scrollTop = 0;
      updateOverflowState();
    }
  }, [selected, updateOverflowState]);

  return (
    <Frame
      ref={frameRef}
      $selected={selected}
      $scale={enlarged ? enlargeScale : 1}
    >
      {leadMedia && <Thumbnail media={leadMedia} />}
      <Body
        ref={bodyRef}
        $scrollable={selected}
        data-card-scroll={selected ? "" : undefined}
        onScroll={updateOverflowState}
      >
        <Title>{card.title}</Title>
        {bodyContent && <CardMarkdown content={bodyContent} />}
      </Body>
      <BottomFade $visible={hasMoreBelow} />
      <TakeLabel
        label={card.category || "TIPS"}
        labelColor={card.is_required ? "blue" : "gray"}
      />
    </Frame>
  );
};

export default TipPortraitCard;
